import { useEffect, useRef, useState } from 'react';

interface UseCopyToClipboardReturn {
  copyToClipboard: (text: string, fieldName: string) => Promise<void>;
  copiedField: string | null;
}

/**
 * Custom hook for copying text to clipboard with visual feedback
 * @param feedbackDuration - Duration in milliseconds for the copied feedback (default: 1500)
 * @returns Object containing copyToClipboard function and copiedField state
 */
export const useCopyToClipboard = (feedbackDuration: number = 1500): UseCopyToClipboardReturn => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);

      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      setCopiedField(fieldName);
      timeoutRef.current = window.setTimeout(() => setCopiedField(null), feedbackDuration);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { copyToClipboard, copiedField };
};
